import type { TelemetryService } from '@ctw/shared/context/telemetry/index';
import type { TelemetryContext } from '@ctw/shared/context/telemetry/telemetry-boundary';

type TelemetryContextMappings = {
  [key in TelemetryService]?: {
    extractKeys?: {
      [key: string]: (value: unknown) => {
        [key: string]: unknown;
      };
    };
    renameKeys?: { [key: string]: string };
    removeKeys?: Array<string>;
  };
};

const telemetryContextMappings: TelemetryContextMappings = {
  amplitude: {
    extractKeys: {
      ancestralBoundaryNames: (boundaryNames) => ({
        parentBoundaryName: Array.isArray(boundaryNames) ? boundaryNames.at(-1) : undefined,
      }),
    },
    renameKeys: {
      serviceEnv: 'env',
      patientUPID: 'upid',
    },
    removeKeys: ['telemetryEnv', 'patientContext', 'ancestralBoundaryNames'],
  },
} as const;

export const mapTelemetryContext = (
  telemetryService: TelemetryService,
  context: TelemetryContext,
): TelemetryContext => {
  if (!(telemetryService in telemetryContextMappings)) {
    return context;
  }

  const newContext = { ...context };

  if (telemetryContextMappings[telemetryService]?.extractKeys) {
    for (const [key, extractionFn] of Object.entries(
      telemetryContextMappings[telemetryService].extractKeys,
    )) {
      const newKeys = extractionFn(newContext[key]);
      for (const [newKey, newValue] of Object.entries(newKeys)) {
        newContext[newKey] = newValue;
      }
    }
  }

  if (telemetryContextMappings[telemetryService]?.renameKeys) {
    for (const [oldKey, newKey] of Object.entries(
      telemetryContextMappings[telemetryService].renameKeys,
    )) {
      newContext[newKey] = newContext[oldKey];
      delete newContext[oldKey];
    }
  }

  if (telemetryContextMappings[telemetryService]?.removeKeys) {
    for (const key of telemetryContextMappings[telemetryService].removeKeys) {
      delete newContext[key];
    }
  }

  return newContext;
};
